import { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/AuthLayout';
import PasswordField from '../components/PasswordField';
import { useAuth } from '../../../context/AuthContext';
import { AUTH_TOKEN_KEY } from '../../../utils/authSession';
import { requestPasswordReset, requestPhoneOtp, verifyPhoneOtp } from '../../../services/authService';
import { authMethods } from '../../../config/authMethods';

const LoginMode = {
  SELECT: 'select',
  PHONE: 'phone',
  EMAIL: 'email',
};

const OTP_RESEND_SECONDS = 30;

const LoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, persistAuth } = useAuth();
  const showEmail = authMethods.emailEnabled;
  const showGoogle = authMethods.googleEnabled;
  const showPhone = authMethods.phoneEnabled;


  const clearedLocationStateRef = useRef(false);

  const [mode, setMode] = useState(() => {
    if (showEmail && showPhone) return LoginMode.SELECT;
    if (showEmail) return LoginMode.EMAIL;
    if (showPhone) return LoginMode.PHONE;
    return LoginMode.SELECT;
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [phoneOtpStep, setPhoneOtpStep] = useState(1);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [phoneForm, setPhoneForm] = useState({ phone: '', otp: '' });
  const [emailForm, setEmailForm] = useState({ email: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');

  const isAnyLoading = isSubmitting || isSendingReset;

  const statusClassName = useMemo(() => {
    if (status.type === 'error') return 'alert alert-danger py-2 mb-0';
    if (status.type === 'pending') return 'alert alert-warning py-2 mb-0';
    return 'alert alert-info py-2 mb-0';
  }, [status.type]);

  useEffect(() => {
    if (otpCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setOtpCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpCooldown]);

  useEffect(() => {
    const hasLogoutSuccess = Boolean(location.state?.logoutSuccess);
    const hasSessionExpired = Boolean(location.state?.sessionExpired);

    if (hasLogoutSuccess) {
      setStatus({ type: 'info', message: 'You have been logged out successfully.' });
    } else if (hasSessionExpired) {
      setStatus({ type: 'info', message: 'Your session expired. Please login again.' });
    }

    if (!hasLogoutSuccess && !hasSessionExpired) {
      clearedLocationStateRef.current = false;
      return;
    }

    if (clearedLocationStateRef.current) return;
    clearedLocationStateRef.current = true;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate]);

  const decodeJwt = (token) => {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      return null;
    }
  };

  const resetStatus = () => setStatus({ type: '', message: '' });
  const setErrorStatus = (message) => setStatus({ type: 'error', message });

  const resolveDefaultRedirect = (payload) => {
    const role = payload?.user?.role;
    if (role === 'individual') return '/individual/tournaments';
    if (role === 'team_owner') return '/team-owner/tournaments';
    return '/profile';
  };

  const handleLoginSuccess = (payload) => {
    if (payload?.user?.approvalStatus === 'pending') {
      setStatus({ type: 'pending', message: 'You are yet to be approved by the department admin.' });
      return;
    }

    const redirectPath = payload?.redirectPath || resolveDefaultRedirect(payload);
    navigate(redirectPath);
  };


  const handleGoogleSuccess = async (credentialResponse) => {
    const credential = credentialResponse?.credential;

    if (!credential) {
      setErrorStatus('Unable to verify your Google account.');
      return;
    }

    const profile = decodeJwt(credential);

    if (!profile?.email || !profile?.sub) {
      setErrorStatus('Unable to read Google profile details. Please try again.');
      return;
    }

    try {
      resetStatus();
      setIsSubmitting(true);
      const payload = await login({
        provider: 'google',
        email: profile.email,
        googleId: profile.sub,
        googleToken: credential,
      });
      handleLoginSuccess(payload);
    } catch (error) {
      setErrorStatus(error.message || 'Google login failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneChange = ({ target: { name, value } }) => {
    resetStatus();
    setPhoneForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmailChange = ({ target: { name, value } }) => {
    resetStatus();
    setEmailForm((prev) => ({ ...prev, [name]: value }));
  };

  const sendPhoneOtp = async (event) => {
    event.preventDefault();
    const phone = phoneForm.phone.trim();
    if (!phone) return setErrorStatus('Phone number is required.');

    try {
      resetStatus();
      setIsSubmitting(true);
      await requestPhoneOtp({ phone, purpose: 'login' });
      setPhoneOtpStep(2);
      setOtpCooldown(OTP_RESEND_SECONDS);
      setStatus({ type: 'info', message: 'OTP sent. Enter the code to continue.' });
    } catch (error) {
      setErrorStatus(error.message || 'Unable to send OTP.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyPhoneLoginOtp = async (event) => {
    event.preventDefault();
    const phone = phoneForm.phone.trim();
    const otp = phoneForm.otp.trim();

    if (!phone) return setErrorStatus('Phone number is required.');
    if (!/^\d{6}$/.test(otp)) return setErrorStatus('Please enter a valid 6-digit OTP.');

    try {
      resetStatus();
      setIsSubmitting(true);
      const payload = await verifyPhoneOtp({ phone, otp, purpose: 'login' });
      if (payload?.token) {
        localStorage.setItem(AUTH_TOKEN_KEY, payload.token);
        sessionStorage.setItem(AUTH_TOKEN_KEY, payload.token);
        persistAuth(payload);
      }
      handleLoginSuccess(payload);
    } catch (error) {
      setErrorStatus(error.message || 'Unable to verify OTP and sign in.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();

    try {
      resetStatus();
      setIsSubmitting(true);
      const payload = await login({
        provider: 'email',
        email: emailForm.email.trim(),
        password: emailForm.password,
      });
      handleLoginSuccess(payload);
    } catch (error) {
      setErrorStatus(error.message || 'Unable to sign in with email.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPasswordSubmit = async (event) => {
    event.preventDefault();

    if (!forgotEmail.trim()) {
      setErrorStatus('Email is required to reset your password.');
      return;
    }

    try {
      resetStatus();
      setIsSendingReset(true);
      await requestPasswordReset({ email: forgotEmail.trim() });
      setStatus({ type: 'info', message: 'If an account exists, you’ll receive reset instructions.' });
    } catch (error) {
      setErrorStatus(error.message || 'Unable to send reset instructions.');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleModeChange = (nextMode) => {
    resetStatus();
    setShowForgotPassword(false);
    setMode(nextMode);
  };

  const renderMethodSelector = () => (
    <div className="d-grid gap-2">
      {showEmail && <button type="button" className="btn btn-outline-primary" onClick={() => handleModeChange(LoginMode.EMAIL)} disabled={isAnyLoading}>Continue with Email</button>}
      {showPhone && <button type="button" className="btn btn-outline-primary" onClick={() => handleModeChange(LoginMode.PHONE)} disabled={isAnyLoading}>Continue with Mobile Number</button>}
    </div>
  );

  const renderPhoneFlow = () => (
    <form onSubmit={phoneOtpStep === 1 ? sendPhoneOtp : verifyPhoneLoginOtp} className="d-grid gap-3">
      <div>
        <label className="form-label" htmlFor="loginPhoneNumber">Phone Number</label>
        <input id="loginPhoneNumber" name="phone" type="tel" className="form-control" placeholder="+1 555 123 4567" value={phoneForm.phone} onChange={handlePhoneChange} required />
      </div>

      {phoneOtpStep === 2 && (
        <div>
          <label className="form-label" htmlFor="loginPhoneOtp">OTP</label>
          <input id="loginPhoneOtp" name="otp" type="text" inputMode="numeric" className="form-control" placeholder="Enter OTP" value={phoneForm.otp} onChange={handlePhoneChange} maxLength={6} required />
        </div>
      )}

      <button type="submit" className="btn btn-primary w-100" disabled={isAnyLoading}>
        {isSubmitting ? (
          <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />{phoneOtpStep === 1 ? 'Sending OTP...' : 'Verifying...'}</>
        ) : phoneOtpStep === 1 ? 'Send OTP' : 'Verify & Sign In'}
      </button>

      {phoneOtpStep === 2 && (
        <button
          type="button"
          className="btn btn-link p-0 text-start"
          onClick={(event) => sendPhoneOtp(event)}
          disabled={isAnyLoading || otpCooldown > 0}
        >
          {otpCooldown > 0 ? `Resend OTP in ${otpCooldown}s` : 'Resend OTP'}
        </button>
      )}
    </form>
  );

  const renderEmailFlow = () => (
    <form onSubmit={handleEmailSubmit} className="d-grid gap-3">
      <div>
        <label className="form-label" htmlFor="loginEmailAddress">Email</label>
        <input id="loginEmailAddress" name="email" type="email" className="form-control" placeholder="you@example.com" value={emailForm.email} onChange={handleEmailChange} required />
      </div>
      <div>
        <label className="form-label" htmlFor="loginEmailPassword">Password</label>
        <PasswordField id="loginEmailPassword" name="password" placeholder="Enter your password" value={emailForm.password} onChange={handleEmailChange} required />
      </div>
      <button type="submit" className="btn btn-primary w-100" disabled={isAnyLoading}>
        {isSubmitting ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Signing in...</> : 'Sign in'}
      </button>
      {showEmail && <button type="button" className="btn btn-link p-0 text-start" onClick={() => { resetStatus(); setShowForgotPassword((prev) => !prev); setForgotEmail(emailForm.email); }} disabled={isAnyLoading}>Forgot password?</button>}

      {showForgotPassword && (
        <div className="border rounded p-3 bg-light d-grid gap-2">
          <label className="form-label mb-0" htmlFor="forgotPasswordEmail">Reset email</label>
          <input id="forgotPasswordEmail" name="forgotEmail" type="email" className="form-control" placeholder="you@example.com" value={forgotEmail} onChange={(event) => setForgotEmail(event.target.value)} required />
          <button type="button" className="btn btn-outline-primary" onClick={handleForgotPasswordSubmit} disabled={isAnyLoading}>
            {isSendingReset ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Sending...</> : 'Send reset link'}
          </button>
        </div>
      )}

      <button type="button" className="btn btn-link p-0 text-start" onClick={() => handleModeChange(LoginMode.SELECT)} disabled={isAnyLoading}>Change method</button>
    </form>
  );

  return (
    <AuthLayout title="Sign in to your account" subtitle="Choose a sign-in method to continue." showLogo>
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4 d-grid gap-3">
          {mode === LoginMode.SELECT && renderMethodSelector()}
          {!showEmail && !showGoogle && !showPhone && <div className='alert alert-warning py-2 mb-0'>No login methods are currently enabled.</div>}
          {showPhone && mode === LoginMode.PHONE && renderPhoneFlow()}
          {showEmail && mode === LoginMode.EMAIL && renderEmailFlow()}
          {showGoogle && <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setErrorStatus('Google login failed.')} />}

          {status.message && <div className={statusClassName} role="status">{status.message}</div>}

          <div className="text-center small">Don&apos;t have an account? <Link to="/auth/register">Create one</Link></div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
